import { useEffect, useState } from 'react'
import { FileAction, useFilesContext } from '../files-context'
import { CustomerState } from 'lib/api/ticket-files/ticket-files'
import { useMutation, useQuery } from '@tanstack/react-query'
import { request } from 'lib/api/fetch-api'
import { toast } from 'lib/components/toast/toast'
import Modal from 'lib/components/modals/modal'
import { ArrowLeftRightIcon, InfoIcon } from 'lucide-react'
import CurrentAsset from './current-asset-screen'
import Checkbox from 'lib/components/checkbox/checkbox'
import Button from 'components/core/button'
import { AUTO_SHOW_FILE_EXPORT_MODAL_KEY } from 'components/pages/request/media/state-button'
import { useUserContext } from 'providers/user-provider'
import TextInput from 'components/core/text-input/text-input'
import SelectBox from 'lib/components/dropdown/select-box'
import Link from 'lib/components/link/link'

const ExportScreen = () => {
  const { files, setMode, onFinish } = useFilesContext()
  const { user } = useUserContext()
  const [key, setKey] = useState('')
  const [selectedCollection, setSelectedCollection] = useState('')
  const [hasStockpressKey, setHasStockpressKey] = useState(user.hasStockpress)

  const [autoShow, setAutoShow] = useState(localStorage.getItem(AUTO_SHOW_FILE_EXPORT_MODAL_KEY) !== 'true')

  const approved = files.every((file) => file?.customerState === CustomerState.Approved)

  const { mutate: exportToStockPress } = useMutation({
    mutationFn: () =>
      request({
        endpoint: 'exportAssets',
        body: { ticket_file_ids: files.map((file) => file.id), collection_uuid: selectedCollection },
      }),
    onSuccess: () => {
      setMode(null)
      onFinish?.({ action: FileAction.UPDATE, ticketFiles: files })
      toast.success(
        'Your file has been exported to Stockpress. It may take a few minutes to appear in your Stockpress account.',
      )
    },
    onError: (err) => {
      toast.error('There was an error exporting to Stockpress, please try again')
      console.error(err)
    },
  })
  const { mutate: addIntegrationKey } = useMutation({
    mutationFn: (key: string) =>
      request({
        endpoint: 'createIntegrationKey',
        body: { data: { name: 'Stockpress Key', platform_type: 1, integration_key: key } },
      }),
    onSuccess: () => {
      toast.success('Your Stockpress integration key has been created. You can now export your assets to Stockpress.')
      setHasStockpressKey(true)
    },
    onError: (err) => {
      toast.error('There was an error exporting to Stockpress, please try again')
      console.error(err)
    },
  })

  const { data: externalAssetCollections, error } = useQuery({
    queryKey: ['externalAssetCollections'],
    queryFn: () => request({ endpoint: 'getExternalAssetCollections' }),
    enabled: hasStockpressKey,
  })

  useEffect(() => {
    if (error) {
      toast.error(
        'There was an error fetching the external asset collections, please try again or check your Stockpress integration key',
      )
    }
  }, [error])

  return (
    <>
      <Modal.Header className="tw-font-bold">Export</Modal.Header>
      <Modal.Body
        className="tw-mt-4 tw-flex tw-flex-col tw-gap-4 tw-overflow-visible tw-text-neutral-800"
        setOpen={() => setMode(null)}
        closeButton
      >
        {!approved && (
          <div className="tw-flex tw-flex-row tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-bg-skyblue-50 tw-p-4 tw-text-sky-700">
            <InfoIcon className="tw-mt-1 tw-h-4 tw-w-4 tw-flex-shrink-0" />
            Only assets marked as approved in the Design Pickle platform are eligible to be sent to Stockpress. To
            export directly to Stockpress, mark your asset as approved.
          </div>
        )}
        <CurrentAsset />
        {hasStockpressKey && externalAssetCollections && !error ? (
          <div className="tw-flex tw-flex-row tw-gap-2 tw-rounded tw-p-3">
            <SelectBox
              label="Stockpress Collection"
              className="tw-w-full"
              disabled={!approved}
              searchable
              options={
                externalAssetCollections?.map((collection) => ({
                  label: collection.name,
                  value: collection.uuid,
                  displayElement: <div className="tw-text-sm">{collection.name}</div>,
                })) || []
              }
              selectedValue={selectedCollection}
              handleChange={(value: string) => setSelectedCollection(value)}
              multiple={false}
            />
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded tw-p-3">
            <div className="tw-text-center tw-text-lg tw-font-bold">
              Design Pickle + Stockpress: From concept to cloud
            </div>
            <div className="tw-text-center">
              Export your approved Design Pickle assets directly to our partners at Stockpress.
            </div>
            <div className="tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-2 tw-p-2">
              <div className="tw-rounded-lg tw-bg-neutral-100 tw-p-2">
                <img src={`/images/vendors/stockpress.png`} width={50} height={50} />
              </div>
              <ArrowLeftRightIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />
              <div className="tw-rounded-lg tw-bg-neutral-100 tw-p-2">
                <img src={'/images/icons/brand-circle.svg'} width={50} height={50} />
              </div>
            </div>
            <div className="tw-text-center">Already have Stockpress? Let&apos;s connect your workspace.</div>
            <div className="tw-text-center">
              Don&apos;t have Stockpress? sign up{' '}
              <Link href="https://stockpress.co/stockpress-x-design-pickle/" target="_blank">
                here
              </Link>{' '}
              and receive free upgraded features just for being a Design Pickle customer
            </div>
            <TextInput
              className="tw-w-full"
              name="stockpress-api-key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
            <Button disabled={!key} className="tw-mx-auto" color="neutral" onClick={() => addIntegrationKey(key)}>
              Connect to Stockpress
            </Button>
          </div>
        )}

        <Checkbox
          label="Automatically show this modal when a file is approved"
          id="file-export-modal-checkbox"
          onClick={(value) => {
            setAutoShow(value.target.checked)
            localStorage.setItem(AUTO_SHOW_FILE_EXPORT_MODAL_KEY, value.target.checked ? 'false' : 'true')
          }}
          isChecked={autoShow}
          disabled={!approved}
        />

        <div className="tw-flex tw-flex-row tw-items-center tw-justify-between">
          <div className="tw-text-sm tw-font-semibold">1 asset selected</div>
          <Button
            color="purple"
            onClick={() => exportToStockPress()}
            disabled={!approved || !selectedCollection || !hasStockpressKey}
          >
            Export to Stockpress
          </Button>
        </div>
      </Modal.Body>
    </>
  )
}

export default ExportScreen
